import { useQuery } from "@tanstack/react-query";
import { collection, query, where, getDocs, type QueryDocumentSnapshot, type DocumentData } from "firebase/firestore";
import { getDb } from "@repo/backend/firebase";
import { BookingSchema } from "../schema/booking.js";
import type { Booking } from "../schema/booking.js";

async function fetchBookings(clientId: string, date: string): Promise<Booking[]> {
  const db = getDb();
  const q = query(
    collection(db, "clients", clientId, "reservations"),
    where("date", "==", date),
    where("status", "in", ["pending", "confirmed"]),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d: QueryDocumentSnapshot<DocumentData>) => BookingSchema.safeParse({ id: d.id, ...d.data() }))
    .filter((r): r is { success: true; data: Booking } => r.success)
    .map((r) => r.data);
}

/**
 * Returns all active (pending/confirmed) reservations for a given date.
 * Used by BookingGrid to mark slots as unavailable.
 *
 * @param clientId - tenant identifier
 * @param date     - ISO date string "YYYY-MM-DD"
 */
export function useBookings(clientId: string, date: string) {
  return useQuery<Booking[]>({
    queryKey: ["client", clientId, "bookings", date],
    queryFn: () => fetchBookings(clientId, date),
    staleTime: 30_000, // 30 s — bookings change frequently
    refetchInterval: 60_000, // background refetch every 60 s
    enabled: !!clientId && !!date,
  });
}
